import { useCallback, useEffect, useRef, useState } from 'react'
import { apiClient } from '../../lib/api/client'
import { usePollWhilePending, useRefetchOnWindowFocus } from '../../lib/useRefetchOnWindowFocus'
import { putFileToS3 } from './s3Upload'
import { StagingSessionNotFoundError } from './staging-types'
import type {
  BomPartLine,
  BomSummary,
  BulkConfigureRequest,
  CadUploadTicket,
  CatalogSearchResult,
  ConfirmStagedPartsResult,
  FileResolutionAction,
  StagingTriage,
  UpdateStagedPartPatch,
} from './staging-types'

/** Pull the `title` off a Problem+JSON error body for a user-facing message. */
function problemTitle(error: unknown, fallback: string): string {
  return typeof error === 'object' && error !== null && 'title' in error
    ? String((error as { title?: unknown }).title)
    : fallback
}

/**
 * The data-layer seam for Staging triage (UID-42). See staging-types.ts's
 * doc comment: shaped field-for-field against the software-engineer's
 * in-progress `StagingTriage` BFF contract so swapping
 * `mockStagingService` (moved to `./test-fixtures.ts` — code-review
 * UID-106 hygiene finding: test doubles must not be reachable from a
 * runtime module) for a real one is a drop-in once that branch merges.
 */
export interface StagingService {
  getStagingTriage(sessionId: string): Promise<StagingTriage>
  confirmMatch(sessionId: string, partId: string): Promise<StagingTriage>
  ignoreMatch(sessionId: string, partId: string): Promise<StagingTriage>
  swapSourcing(sessionId: string, partId: string, optionId: string): Promise<StagingTriage>
  /**
   * ADR-0146 D11 step 4/5 — the catalog-leaf sourcing dropdown's write path.
   * Keyed on the BOM node (`nodeId`), NEVER on `partId` — a catalog leaf's
   * `partId` is minted from its SKU and churns on every selection (D7), and
   * a catalog leaf has no `intake.staged_file` row for `swapSourcing` above
   * to write through in the first place (D3). Hits the FIRST node-subject
   * staging route, `POST .../bom-nodes/:nodeId/sourcing` with
   * `{ action: 'select', optionId }`. See
   * {@link StagingService.keepCatalogNodeBomReference} for the `keep` arm,
   * which UID-496 wires — the claim that used to sit here ("the design has
   * no state/copy for it, D4/OQ-C") is FALSE against the authoritative
   * design and is corrected there rather than repeated here.
   */
  selectCatalogNodeSourcingOption(
    sessionId: string,
    nodeId: string,
    optionId: string,
  ): Promise<StagingTriage>
  /**
   * UID-496 — the SAME node-subject route's `keep` arm
   * (`{ action: 'keep' }` → `keepCatalogLeafBomReference`, the
   * `'kept-bom-reference'` decision of migration 1014). Node-keyed for the
   * identical reason `selectCatalogNodeSourcingOption` above is: a catalog
   * leaf's `partId` is minted from its SKU and churns on every selection
   * (ADR-0146 D7), so it is not a stable handle for a decision about a BOM
   * position.
   *
   * WHY THIS EXISTS NOW, when the comment above spent two ADRs explaining
   * that it deliberately did not. ADR-0146 D4's stated premise was that the
   * catalog arm has "no design state and no approved copy" for Keep, routing
   * real copy to the product-designer's OQ-C. **That premise does not hold
   * against the authoritative design** (`design_handoff_misumi_unified_platform/
   * Fictiv Platform.dc.html`): the BOM-row builder `stdRow` (:9384) sets
   * `onKeep`/`keepSel`/`kept`/`keptMeta`, and the "Keep my uploaded part"
   * row (:3242) renders INSIDE the `bomRows` loop (:3165) — i.e. on exactly
   * this population. The design's Keep is `revertMatch` (:6258): it clears
   * the sourcing selection and toasts `Keeping your uploaded <name>`, which
   * is the transient form of the durable `'kept-bom-reference'` arm the
   * server has carried since migration 1014.
   *
   * Verified narrower, and still TRUE: `stdRow` sets no `cadFile`/`hasCad`,
   * so a catalog leaf genuinely carries no CAD file. That supports "no CAD
   * file"; it does not support "no design state for Keep".
   */
  keepCatalogNodeBomReference(sessionId: string, nodeId: string): Promise<StagingTriage>
  /**
   * UID-496 / ADR-0162 D3.2 — the SAME node-subject route's `ignore` and
   * `unignore` arms.
   *
   * DISTINCT FROM {@link StagingService.ignoreMatch} above, which is
   * `partId`-scoped and writes `staged_file.match_status` — a catalog leaf has
   * no `staged_file` row, so that verb 404s for this population. These write
   * `staged_bom_node.match_ignored_at` (migration 1021) instead.
   *
   * A TOGGLE, and losslessly so: `unignore` restores nothing because `ignore`
   * destroyed nothing. Any sourcing decision the human made (their selected
   * option, or an explicit keep) lives in a DIFFERENT table and is untouched by
   * both arms — which is the property ADR-0162 D3.3(1) shows a third value of
   * migration 1014's `decision` could not have had.
   *
   * `unignore` on a node that is not currently ignored is a successful no-op,
   * not an error (ADR-0157 Am.1 D3's precedent), so a double-click cannot
   * surface a spurious failure to the user.
   */
  ignoreCatalogNodeMatch(sessionId: string, nodeId: string): Promise<StagingTriage>
  unignoreCatalogNodeMatch(sessionId: string, nodeId: string): Promise<StagingTriage>
  bulkConfigure(sessionId: string, request: BulkConfigureRequest): Promise<StagingTriage>
  /** UID-188 — set the editable Qty on an identified part. */
  updateQuantity(sessionId: string, partId: string, quantity: number): Promise<StagingTriage>
  /**
   * ADR-0156 D1/D5 — the ONE node-scoped quantity verb behind the editable
   * Qty cell on every matched/catalog/unresolved BOM leaf row. Keyed on
   * `nodeId`, NEVER the SKU-derived `partId` (ADR-0146 D7's identity
   * hazard). NOT used by the identified arm — that stays `updateQuantity`
   * above (D1.1: neither node quantity column is ever read/written there).
   * 404 unknown/foreign node; 409 an assembly node (its `Qty = N` is
   * DERIVED); 400 schema. Server-authoritative — the 200 body is the full
   * re-projected triage (D4), no optimistic update.
   */
  updateNodeQuantity(sessionId: string, nodeId: string, quantity: number): Promise<StagingTriage>
  /**
   * ADR-0157 D2 — the ONE node-scoped verb behind BOTH a catalog leaf's hard
   * Remove and an unresolved leaf's soft Dismiss (D2.1's table: "catalog"
   * and "unresolved" both call the SAME new route; the server decides
   * hard-vs-soft by arm — a `nodeId` is never enough on its own to know
   * which, and this client never guesses). Keyed on `nodeId`, never `partId`
   * (D2.1: the frontend must not pick a destructive verb's fan-out by
   * namespace-sniffing a path parameter). 404 unknown/foreign node, 409 an
   * assembly node or an identified/matched (resolved, non-catalog) leaf —
   * those keep `removeStagedPart` above.
   *
   * CONTRACT LANDED 2026-09-06 — `DELETE /uploads/{sessionId}/staging/
   * bom-nodes/{nodeId}` (`operationId: removeStagedBomNode`), no request
   * body, `200` → the full `StagingTriage`, `401`/`403`/`404`/`409`. The ARM
   * DISPATCH (hard vs. soft) happens SERVER-SIDE off the node's render
   * state — this client sends nothing to pick it, and the caller reads the
   * result off the refreshed triage: a node absent from it was hard
   * removed; a node present with `dismissedAt` set was soft dismissed.
   */
  removeBomNode(sessionId: string, nodeId: string): Promise<StagingTriage>
  /**
   * ADR-0157 D3.7 SUPERSEDED (architect, 2026-09-06) — a restore verb IS
   * authorized. Landed as `DELETE /uploads/{sessionId}/staging/bom-nodes/
   * {nodeId}/dismissal` (a resource `DELETE` on the dismissal itself,
   * idempotent). Keyed on `nodeId`. MUST NEVER be offered by the caller for
   * a hard-removed (catalog, resolved) leaf — there is nothing to restore;
   * only a leaf that came back from `removeBomNode` with `dismissedAt` set
   * may show this affordance.
   */
  restoreBomNode(sessionId: string, nodeId: string): Promise<StagingTriage>
  /** UID-222 #4 — set (or clear, via `null`) the per-part material on an
   * identified part, independent of a full bulk-configure. Both this and
   * `updateQuantity` hit the same `PATCH .../parts/:partId` endpoint
   * (`UpdateStagedPartQuantityRequest`, both fields optional). */
  updateMaterial(sessionId: string, partId: string, material: string | null): Promise<StagingTriage>
  /** UID-222 #7 — remove an identified OR matched staged part.
   * Server-authoritative; the part does not reappear on refetch. */
  removeStagedPart(sessionId: string, partId: string): Promise<StagingTriage>
  /** UID-188 — attach an existing (files-to-resolve) drawing to an identified part. */
  associateDrawing(sessionId: string, partId: string, fileId: string): Promise<StagingTriage>
  /** UID-188 — generate a drawing from the CAD model. Rejects with a clear
   * "not available yet" message for a genuinely real (catalog/configurable)
   * identified part — never fabricates a drawing on one of those, still true.
   *
   * UID-554: for a `type: 'custom'` identified part, this now SUCCEEDS — the
   * same demo fabrication `generateNodeDrawing` below already does for a
   * custom BOM leaf with no `partId`, extended to the identified arm because
   * UID-535's citation pass can route a custom leaf into it. */
  generateDrawing(sessionId: string, partId: string): Promise<StagingTriage>
  /** UID-222 #9-upload — real presign-then-PUT flow for a NEW drawing file:
   * mints a `CadUploadTicket` via the drawing-action endpoint, then PUTs
   * `file`'s bytes directly to S3 (reusing the UID-40 `putFileToS3` seam),
   * then refetches the triage. */
  uploadDrawing(sessionId: string, partId: string, file: File): Promise<StagingTriage>
  /**
   * UID-497 (T10) — the CUSTOM ("Make") BOM leaf's own drawing verbs, keyed by
   * the row's stable `nodeId`.
   *
   * WHY THEY ARE NOT `associateDrawing`/`generateDrawing`/`uploadDrawing`
   * ABOVE. Those four are `partId`-scoped and act on a `staged_file` row. A
   * custom BOM leaf has none (its `partId` is `null`), so they 404 for exactly
   * the population these exist for. Same reason `updateNodeQuantity` sits
   * beside `updateQuantity`.
   *
   * `associateNodeDrawing` names the drawing by FILENAME, not `fileId`: the
   * server re-resolves it against this session's own drawing pool (404 when it
   * is not there), so the filename is a selection rather than an assertion.
   */
  associateNodeDrawing(sessionId: string, nodeId: string, filename: string): Promise<StagingTriage>
  detachNodeDrawing(sessionId: string, nodeId: string): Promise<StagingTriage>
  /** The DEMO generate arm. The server derives the filename from the node's own
   * name (`Mounting Base` -> `mounting_base_generated.pdf`) — the client never
   * supplies it. UID-554: `generateDrawing` above now does the SAME
   * fabrication for a `type: 'custom'` identified part; this one remains the
   * only path for a custom leaf that never resolved to a `partId` at all. */
  generateNodeDrawing(sessionId: string, nodeId: string): Promise<StagingTriage>
  /** Presign -> direct-to-S3 PUT -> register, the same three steps
   * `uploadDrawing` uses, against the node route. PDF-only server-side. */
  uploadNodeDrawing(sessionId: string, nodeId: string, file: File): Promise<StagingTriage>
  /** UID-497 (T10) — write `staged_bom_node.selected_material`. NEVER touches
   * the customer's verbatim `statedMaterial`; `null` clears the selection. */
  updateNodeMaterial(
    sessionId: string,
    nodeId: string,
    material: string | null,
  ): Promise<StagingTriage>
  /** UID-190 — resolve a files-to-resolve row (server-authoritative; survives
   * refetch). `assign` requires `targetPartId`; `match` accepts an optional
   * `catalogId` (UID-222 #3, from `searchStagingCatalog`) to label the
   * produced Matched part with a chosen DEMO catalog suggestion. */
  resolveFile(
    sessionId: string,
    fileId: string,
    action: FileResolutionAction,
    targetPartId?: string,
    catalogId?: string,
  ): Promise<StagingTriage>
  /** UID-222 #3 — search the MOCK/DEMO catalog for the "Match to catalog"
   * inline picker on a `no-match` files-to-resolve row. DEMO DATA ONLY. */
  searchStagingCatalog(sessionId: string, query: string): Promise<CatalogSearchResult>
  /** UID-191 — confirm the selected matched parts into the Library (terminal). */
  confirmParts(sessionId: string, partIds: readonly string[]): Promise<ConfirmStagedPartsResult>
  /** UID-222 #1 — list the caller's BOMs for the Add-to-BOM picker. */
  listBoms(): Promise<readonly BomSummary[]>
  /** UID-222 #1 — create a new empty BOM, so the Add-to-BOM flow can add lines next. */
  createBom(name: string): Promise<BomSummary>
  /** UID-191 — add confirmed Library parts to an existing BOM as lines. Composes
   * the existing `POST /boms/{bomId}/lines` (no new contract). */
  addPartsToBom(bomId: string, lines: readonly BomPartLine[]): Promise<void>
}

async function bffGetStagingTriage(sessionId: string): Promise<StagingTriage> {
  const { data, error, response } = await apiClient.GET('/uploads/{sessionId}/staging', {
    params: { path: { sessionId } },
  })
  if (response.status === 404) {
    throw new StagingSessionNotFoundError(sessionId)
  }
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load this session.'))
  }
  return data
}

async function bffConfirmMatch(sessionId: string, partId: string): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST('/uploads/{sessionId}/staging/confirm', {
    params: { path: { sessionId } },
    body: { partId },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t confirm this match. Try again.'))
  }
  return data
}

async function bffIgnoreMatch(sessionId: string, partId: string): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST('/uploads/{sessionId}/staging/ignore', {
    params: { path: { sessionId } },
    body: { partId },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t ignore this match. Try again.'))
  }
  return data
}

async function bffSwapSourcing(
  sessionId: string,
  partId: string,
  optionId: string,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST('/uploads/{sessionId}/staging/swap', {
    params: { path: { sessionId } },
    body: { partId, optionId },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t swap this sourcing option. Try again.'))
  }
  return data
}

/** ADR-0146 D11 step 4/5 — see `StagingService.selectCatalogNodeSourcingOption`'s
 * doc comment. Sends the `select` arm; the `keep` arm is
 * {@link bffKeepCatalogNodeBomReference} below (UID-496). */
async function bffSelectCatalogNodeSourcingOption(
  sessionId: string,
  nodeId: string,
  optionId: string,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/sourcing',
    {
      params: { path: { sessionId, nodeId } },
      body: { action: 'select', optionId },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t record that sourcing choice. Try again.'))
  }
  return data
}

/** UID-496 — the `keep` arm of the same node-subject route. See
 * `StagingService.keepCatalogNodeBomReference`'s doc comment for why this arm
 * is wired now when ADR-0146 D4 declined to wire it. Carries NO `optionId`:
 * `keep` is the OTHER value of migration 1014's single `decision` per node,
 * not a selection. */
async function bffKeepCatalogNodeBomReference(
  sessionId: string,
  nodeId: string,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/sourcing',
    {
      params: { path: { sessionId, nodeId } },
      body: { action: 'keep' },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t keep that BOM line. Try again.'))
  }
  return data
}

/** UID-496 / ADR-0162 D3.2 — the `ignore` and `unignore` arms of the same
 * node-subject route. Both carry NO body beyond the arm name: the node is the
 * path parameter and the instant is server-assigned, so a client cannot
 * backdate a rejection. See `StagingService.ignoreCatalogNodeMatch`'s doc
 * comment for why these are distinct from the `partId`-scoped `ignoreMatch`. */
async function bffSetCatalogNodeMatchIgnored(
  sessionId: string,
  nodeId: string,
  action: 'ignore' | 'unignore',
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/sourcing',
    {
      params: { path: { sessionId, nodeId } },
      body: { action },
    },
  )
  if (error || !data) {
    throw new Error(
      problemTitle(
        error,
        action === 'ignore'
          ? 'We couldn’t ignore that match. Try again.'
          : 'We couldn’t restore that match. Try again.',
      ),
    )
  }
  return data
}

/** W1 fix — the wire contract's `certificates` is a string ARRAY, each
 * entry `minLength:1`/`maxLength:120` (apps/api/openapi.dist.yaml). The UI
 * collects ONE free-text, comma-separated field (e.g. "material cert, RoHS"),
 * which previously got wrapped whole as a single array element — a
 * comma-separated entry over 120 chars would 400, and multiple certificates
 * collapsed into one lossy entry. Split on commas, trim, drop empties, and
 * clamp each entry to the contract's max so a valid-looking submission can
 * never trip the length guard. */
const CERTIFICATE_MAX_LENGTH = 120

function toBulkConfigureCertificates(input: string | undefined): string[] | undefined {
  if (!input) return undefined
  const entries = input
    .split(',')
    .map((entry) => entry.trim().slice(0, CERTIFICATE_MAX_LENGTH))
    .filter((entry) => entry.length > 0)
  return entries.length > 0 ? entries : undefined
}

async function bffBulkConfigure(
  sessionId: string,
  request: BulkConfigureRequest,
): Promise<StagingTriage> {
  // UID-222 #12 — `inspection`/`certificates` are now real, additive fields
  // on `BulkConfigureRequest` and are sent through rather than dropped.
  const certificates = toBulkConfigureCertificates(request.certificates)
  const { data, error } = await apiClient.POST('/uploads/{sessionId}/staging/bulk-configure', {
    params: { path: { sessionId } },
    body: {
      partIds: [...request.partIds],
      process: request.process,
      material: request.material,
      finish: request.finish,
      quantity: request.quantity,
      // The wire contract's `inspection` is an open-vocabulary STRING (not a
      // boolean) — the UI collects a single checkbox, so map it onto the
      // shape the BFF actually persists.
      ...(request.inspection ? { inspection: 'Special inspection requested' } : {}),
      ...(certificates ? { certificates } : {}),
    },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t configure these parts. Try again.'))
  }
  return data
}

async function bffUpdateStagedPart(
  sessionId: string,
  partId: string,
  patch: UpdateStagedPartPatch,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.PATCH('/uploads/{sessionId}/staging/parts/{partId}', {
    params: { path: { sessionId, partId } },
    body: patch,
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t update that part. Try again.'))
  }
  return data
}

async function bffUpdateQuantity(
  sessionId: string,
  partId: string,
  quantity: number,
): Promise<StagingTriage> {
  return bffUpdateStagedPart(sessionId, partId, { quantity })
}

/** ADR-0156 D1/D5(1) — `PATCH /uploads/:sessionId/staging/bom-nodes/:nodeId/quantity`. */
async function bffUpdateNodeQuantity(
  sessionId: string,
  nodeId: string,
  quantity: number,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.PATCH(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/quantity',
    {
      params: { path: { sessionId, nodeId } },
      body: { quantity },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t update that quantity. Try again.'))
  }
  return data
}

/** ADR-0157 D2 — `DELETE /uploads/:sessionId/staging/bom-nodes/:nodeId`
 * (`operationId: removeStagedBomNode`). Arm dispatch (hard remove vs. soft
 * dismiss) happens server-side; this call sends nothing to pick it. */
async function bffRemoveBomNode(sessionId: string, nodeId: string): Promise<StagingTriage> {
  const { data, error } = await apiClient.DELETE(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}',
    {
      params: { path: { sessionId, nodeId } },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t remove this BOM line. Try again.'))
  }
  return data
}

/**
 * ADR-0157 D3.7 SUPERSEDED — the software engineer's landed shape:
 * `DELETE /uploads/:sessionId/staging/bom-nodes/:nodeId/dismissal`. A
 * resource `DELETE` on the dismissal itself (never a `POST .../restore`) —
 * *"remove the dismissal"*, which carries the idempotency Undo needs
 * (calling it twice on an already-live node is a no-op, not a second
 * write). The caller (`StagingPage`) is responsible for only ever offering
 * this on a node that came back from `removeBomNode` with `dismissedAt`
 * set — never on a hard-removed (catalog, resolved) leaf, which has no
 * dismissal to remove and 404s.
 */
async function bffRestoreBomNode(sessionId: string, nodeId: string): Promise<StagingTriage> {
  const { data, error } = await apiClient.DELETE(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/dismissal',
    {
      params: { path: { sessionId, nodeId } },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t undo that dismissal. Try again.'))
  }
  return data
}

async function bffUpdateMaterial(
  sessionId: string,
  partId: string,
  material: string | null,
): Promise<StagingTriage> {
  return bffUpdateStagedPart(sessionId, partId, { material })
}

async function bffRemoveStagedPart(sessionId: string, partId: string): Promise<StagingTriage> {
  const { data, error } = await apiClient.DELETE('/uploads/{sessionId}/staging/parts/{partId}', {
    params: { path: { sessionId, partId } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t remove this part. Try again.'))
  }
  return data
}

async function bffDrawingAction(
  sessionId: string,
  partId: string,
  action: 'associate' | 'generate',
  fileId: string | undefined,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/parts/{partId}/drawing',
    {
      params: { path: { sessionId, partId } },
      body: fileId !== undefined ? { action, fileId } : { action },
    },
  )
  if (error || !data) {
    // generate returns a 501 Problem — surface its title verbatim so the FE
    // can show the "not available yet" message rather than a generic error.
    throw new Error(problemTitle(error, 'We couldn’t update the drawing. Try again.'))
  }
  // The endpoint's 2xx union also includes `CadUploadTicket` (the `upload`
  // action's 201 response) — this function is only ever called with
  // `associate`/`generate`, both of which return `StagingTriage` (200).
  return data as StagingTriage
}

/** UID-222 #9-upload — mints a `CadUploadTicket` for a NEW drawing file via
 * the drawing-action endpoint (201). Distinct from `bffDrawingAction`
 * because the `upload` action's success response is a `CadUploadTicket`,
 * not a `StagingTriage`. */
async function bffRequestDrawingUploadTicket(
  sessionId: string,
  partId: string,
  file: File,
): Promise<CadUploadTicket> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/parts/{partId}/drawing',
    {
      params: { path: { sessionId, partId } },
      body: {
        action: 'upload',
        filename: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t start the drawing upload. Try again.'))
  }
  return data as CadUploadTicket
}

/** C1 fix — the two-step upload journey's second (register) step, called
 * AFTER the browser's PUT to S3 completes. The server HeadObject-verifies
 * the upload landed and associates it onto the part; a `409` means the
 * object hasn't shown up yet (eventual-consistency miss), surfaced through
 * `runMutation`'s error path rather than swallowed. */
async function bffRegisterDrawingUpload(
  sessionId: string,
  partId: string,
  objectKey: string,
  filename: string,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/parts/{partId}/drawing',
    {
      params: { path: { sessionId, partId } },
      body: { action: 'register', objectKey, filename },
    },
  )
  if (error || !data) {
    throw new Error(
      problemTitle(
        error,
        'We couldn’t confirm the drawing upload finished. Try again in a moment.',
      ),
    )
  }
  return data as StagingTriage
}

async function bffUploadDrawing(
  sessionId: string,
  partId: string,
  file: File,
): Promise<StagingTriage> {
  const ticket = await bffRequestDrawingUploadTicket(sessionId, partId, file)
  // `CadUploadTicket` (this endpoint) carries no `maxBytes` — the server
  // already validated `sizeBytes` before signing the ticket, so there's no
  // separate client-side ceiling to enforce here (`S3PutTicket.maxBytes` is
  // an `assertWithinMaxBytes`-only field; `putFileToS3` itself never reads
  // it) — `Number.POSITIVE_INFINITY` satisfies the type without a check.
  await putFileToS3(file, { ...ticket, maxBytes: Number.POSITIVE_INFINITY })
  // C1 fix: `upload` only mints the presigned ticket — the drawing does NOT
  // actually attach to the part until `register` (needs `objectKey` +
  // `filename`, both already in hand) is called and the server
  // HeadObject-verifies the PUT landed. Without this step the upload was a
  // silent no-op: the file reached S3 but the part never showed a
  // `drawingFilename`/`drawingObjectKey`, even after a refetch.
  return bffRegisterDrawingUpload(sessionId, partId, ticket.objectKey, file.name)
}

/**
 * UID-497 (T10) — the node-scoped drawing action, the `bffDrawingAction`
 * counterpart for a CUSTOM BOM leaf. One function for the three arms whose
 * success response is a `StagingTriage`; `upload` has its own (201
 * `CadUploadTicket`), exactly as on the part-scoped seam.
 */
async function bffBomNodeDrawingAction(
  sessionId: string,
  nodeId: string,
  body:
    | { action: 'associate'; filename: string }
    | { action: 'detach' }
    | { action: 'generate' }
    | { action: 'register'; objectKey: string; filename: string },
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/drawing',
    { params: { path: { sessionId, nodeId } }, body },
  )
  if (error || !data) {
    // Surface the Problem title verbatim: a 404 here means "that drawing is
    // not in this session" and a 415 means "not a PDF" — both are things the
    // human can act on, unlike a generic failure message.
    throw new Error(problemTitle(error, 'We couldn’t update the drawing. Try again.'))
  }
  return data as StagingTriage
}

async function bffUploadNodeDrawing(
  sessionId: string,
  nodeId: string,
  file: File,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/drawing',
    {
      params: { path: { sessionId, nodeId } },
      body: {
        action: 'upload',
        filename: file.name,
        // `application/pdf` rather than `application/octet-stream` as the
        // fallback: this arm is PDF-only server-side, and a browser that
        // reports no type for a `.pdf` the user just picked would otherwise be
        // refused for a reason the human cannot see or fix. The FILENAME half
        // of the server's check still has to hold, and the bytes are still
        // whatever they are — this only stops an empty `File.type` from being
        // read as a positive claim that the file is NOT a PDF.
        contentType: file.type || 'application/pdf',
        sizeBytes: file.size,
      },
    },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t start the drawing upload. Try again.'))
  }
  const ticket = data as CadUploadTicket
  // Same reasoning as `bffUploadDrawing`: the server validated `sizeBytes`
  // before signing, so there is no separate client-side ceiling to enforce.
  await putFileToS3(file, { ...ticket, maxBytes: Number.POSITIVE_INFINITY })
  // The PUT alone attaches nothing — `register` is what makes the drawing show
  // after a refetch, and the server HeadObject-verifies the bytes landed first.
  return bffBomNodeDrawingAction(sessionId, nodeId, {
    action: 'register',
    objectKey: ticket.objectKey,
    filename: file.name,
  })
}

async function bffUpdateNodeMaterial(
  sessionId: string,
  nodeId: string,
  material: string | null,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.PATCH(
    '/uploads/{sessionId}/staging/bom-nodes/{nodeId}/material',
    { params: { path: { sessionId, nodeId } }, body: { material } },
  )
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t update the material. Try again.'))
  }
  return data
}

async function bffResolveFile(
  sessionId: string,
  fileId: string,
  action: FileResolutionAction,
  targetPartId: string | undefined,
  catalogId: string | undefined,
): Promise<StagingTriage> {
  const { data, error } = await apiClient.POST('/uploads/{sessionId}/staging/resolve', {
    params: { path: { sessionId } },
    body: {
      fileId,
      action,
      ...(targetPartId !== undefined ? { targetPartId } : {}),
      ...(catalogId !== undefined ? { catalogId } : {}),
    },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t resolve this file. Try again.'))
  }
  return data
}

async function bffSearchStagingCatalog(
  sessionId: string,
  query: string,
): Promise<CatalogSearchResult> {
  const { data, error } = await apiClient.GET('/uploads/{sessionId}/staging/catalog-search', {
    params: { path: { sessionId }, query: { q: query } },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t search the catalog. Try again.'))
  }
  return data
}

async function bffListBoms(): Promise<readonly BomSummary[]> {
  const { data, error } = await apiClient.GET('/boms')
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t load your BOMs. Try again.'))
  }
  return data.boms.map((bom) => ({ id: bom.id, name: bom.name, partCount: bom.partCount }))
}

async function bffCreateBom(name: string): Promise<BomSummary> {
  const { data, error } = await apiClient.POST('/boms', { body: { name } })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t create that BOM. Try again.'))
  }
  return { id: data.id, name: data.name, partCount: data.partCount }
}

async function bffConfirmParts(
  sessionId: string,
  partIds: readonly string[],
): Promise<ConfirmStagedPartsResult> {
  const { data, error } = await apiClient.POST('/uploads/{sessionId}/staging/confirm-parts', {
    params: { path: { sessionId } },
    body: { partIds: [...partIds] },
  })
  if (error || !data) {
    throw new Error(problemTitle(error, 'We couldn’t confirm these parts. Try again.'))
  }
  return data
}

async function bffAddPartsToBom(bomId: string, lines: readonly BomPartLine[]): Promise<void> {
  // The BFF add-line endpoint takes one line and returns the whole BOM; add each
  // selected part sequentially so a mid-batch failure surfaces (and the earlier
  // lines are already durably added — the caller refetches the BOM).
  for (const line of lines) {
    const { error } = await apiClient.POST('/boms/{bomId}/lines', {
      params: { path: { bomId } },
      body: { sourceableId: line.sourceableId, name: line.name, quantity: line.quantity },
    })
    if (error) {
      throw new Error(problemTitle(error, 'We couldn’t add a part to this BOM. Try again.'))
    }
  }
}

/**
 * REAL BFF-backed staging service. Every method maps to a real endpoint;
 * `resolveFile` is server-authoritative (UID-190 `POST .../staging/resolve`).
 * `generateDrawing` calls the same endpoint the demo-stub used to
 * unconditionally 501 on — as of UID-554 it succeeds for a `type: 'custom'`
 * identified part (fabricated demo drawing) and still 501s for a genuinely
 * real one; `uploadDrawing` (UID-222 #9) is a REAL presign-then-PUT flow.
 * `removeStagedPart`/`updateMaterial`/`searchStagingCatalog`/`listBoms`/
 * `createBom` are all UID-222 additions, every one backed by a real endpoint.
 */
export const defaultStagingService: StagingService = {
  getStagingTriage: bffGetStagingTriage,
  confirmMatch: bffConfirmMatch,
  ignoreMatch: bffIgnoreMatch,
  swapSourcing: bffSwapSourcing,
  selectCatalogNodeSourcingOption: bffSelectCatalogNodeSourcingOption,
  keepCatalogNodeBomReference: bffKeepCatalogNodeBomReference,
  ignoreCatalogNodeMatch: (sessionId, nodeId) =>
    bffSetCatalogNodeMatchIgnored(sessionId, nodeId, 'ignore'),
  unignoreCatalogNodeMatch: (sessionId, nodeId) =>
    bffSetCatalogNodeMatchIgnored(sessionId, nodeId, 'unignore'),
  bulkConfigure: bffBulkConfigure,
  updateQuantity: bffUpdateQuantity,
  updateNodeQuantity: bffUpdateNodeQuantity,
  removeBomNode: bffRemoveBomNode,
  restoreBomNode: bffRestoreBomNode,
  updateMaterial: bffUpdateMaterial,
  removeStagedPart: bffRemoveStagedPart,
  associateDrawing: (sessionId, partId, fileId) =>
    bffDrawingAction(sessionId, partId, 'associate', fileId),
  generateDrawing: (sessionId, partId) =>
    bffDrawingAction(sessionId, partId, 'generate', undefined),
  uploadDrawing: bffUploadDrawing,
  associateNodeDrawing: (sessionId, nodeId, filename) =>
    bffBomNodeDrawingAction(sessionId, nodeId, { action: 'associate', filename }),
  detachNodeDrawing: (sessionId, nodeId) =>
    bffBomNodeDrawingAction(sessionId, nodeId, { action: 'detach' }),
  generateNodeDrawing: (sessionId, nodeId) =>
    bffBomNodeDrawingAction(sessionId, nodeId, { action: 'generate' }),
  uploadNodeDrawing: bffUploadNodeDrawing,
  updateNodeMaterial: bffUpdateNodeMaterial,
  resolveFile: (sessionId, fileId, action, targetPartId, catalogId) =>
    bffResolveFile(sessionId, fileId, action, targetPartId, catalogId),
  searchStagingCatalog: bffSearchStagingCatalog,
  confirmParts: bffConfirmParts,
  listBoms: bffListBoms,
  createBom: bffCreateBom,
  addPartsToBom: bffAddPartsToBom,
}

export type StagingState =
  | { readonly status: 'loading' }
  | { readonly status: 'not_found' }
  | { readonly status: 'error'; readonly message: string }
  | { readonly status: 'ready'; readonly triage: StagingTriage }

export interface UseStagingResult {
  readonly state: StagingState
  /** True once the real `getStagingTriage` request has already resolved
   * (or failed) and only the `minLoadingMs` floor timer is left running —
   * `state.status` is still `'loading'` at that point, but the work is
   * genuinely done. Lets a caller distinguish "still actually waiting on
   * the network" from "just holding the loading state open a little
   * longer for perceived-progress reasons" — e.g. snapping a simulated
   * progress animation to 100% instead of continuing to fake movement.
   * Always `false` when `minLoadingMs` is `0`. */
  readonly settling: boolean
  readonly refetch: () => void
  readonly applyMutation: (mutate: () => Promise<StagingTriage>) => Promise<void>
}

/** Loads a session's staging triage and exposes `applyMutation` so every
 * action (confirm/ignore/swap/bulk-configure/resolve-file) updates the
 * screen's state directly from the mutation's response, matching how a
 * real BFF call would return the updated `StagingTriage`.
 *
 * `minLoadingMs` (default `0`, i.e. no change from before) sets a MINIMUM
 * time `status` stays `'loading'` before switching to `ready`/`error`/
 * `not_found` — it never slows the actual request, only delays the
 * observable state transition once the request has already resolved.
 * Deliberately opt-in per call site, not a hook-wide default: a fast local
 * backend can resolve `getStagingTriage` in well under 100ms, which makes
 * the "Analyzing your files" state (rendered while `status === 'loading'`)
 * flash past too quickly to register on an unthrottled connection — visible
 * fidelity loss against the design's own `runAnalysis()` (`Fictiv Platform
 * .dc.html:8214-8219`), which deliberately animates for ~1.5-2.5s regardless
 * of how fast the (simulated) work actually is. `StagingPage` only passes a
 * nonzero value for the ONE path this floor is meant for — arriving fresh
 * from an upload, where this loading state is meant to run visibly BEHIND
 * the "Add these files to a project" modal (UID-509). Every other caller
 * (direct navigation to an existing session, `refetch()`, and — critically —
 * every existing test) keeps the default `0` and is byte-for-byte
 * unaffected; an earlier attempt at a hook-wide floor broke 135 tests across
 * this vertical whose `findByText`/`waitFor` timeouts assume near-immediate
 * resolution.
 *
 * `waitForUpload` (UID-583, ADR-0173 D3, default `false` — no change from
 * before) HOLDS the triage read entirely: while it is `true`, `status` stays
 * `'loading'` and `getStagingTriage` is never called. This is not a cosmetic
 * delay like `minLoadingMs`, it is a CORRECTNESS gate. The flow now navigates
 * here as soon as the session exists (ADR-0173 D1), which is before the
 * presigned PUTs and before `completeUploadSession` has captured the BOM
 * hierarchy — and there is no polling seam to pick that up afterwards
 * (ADR-0134 R2, still open). A read fired in that window returns a session
 * whose rows have no hierarchy, and it would be the LAST word the user gets.
 *
 * The two knobs compose and are not alternatives: `waitForUpload` covers the
 * real work, `minLoadingMs` covers the flash-of-results once the work is
 * done. */
export function useStaging(
  sessionId: string,
  service: StagingService = defaultStagingService,
  minLoadingMs = 0,
  waitForUpload = false,
): UseStagingResult {
  const [state, setState] = useState<StagingState>({ status: 'loading' })
  const [settling, setSettling] = useState(false)
  const [generation, setGeneration] = useState(0)
  const serviceRef = useRef(service)
  serviceRef.current = service
  // UID-335 — distinguishes a genuine navigation to a DIFFERENT session (must
  // show loading — the old session's data is irrelevant) from a same-session
  // background refetch (`refetch()`'s generation bump, incl. the automatic
  // focus/delay ones below): only the former resets `state` to `loading`. A
  // silent background refetch keeps showing the current data until the new
  // fetch resolves, so a background thumbnail catching up never flashes the
  // whole page back to its loading skeleton.
  const prevSessionIdRef = useRef<string | null>(null)
  // UID-509 follow-up — `useRefetchOnWindowFocus` (below) fires `refetch()`
  // on ANY tab-focus regardless of `state.status`, so a generation bump can
  // land WHILE the initial `minLoadingMs` floor is still pending (e.g. the
  // user alt-tabs away and back during the "Analyzing your files" window).
  // That reruns this effect with `sessionChanged` now false (this same
  // session was already recorded), which — using `sessionChanged` alone as
  // the floor gate — would have skipped the floor entirely on that rerun's
  // `settle()` call and let a fast resolve flash straight to `ready`,
  // reintroducing the exact flash-of-results bug this floor exists to
  // prevent. `settledOnceRef` tracks "have we ever left `loading` for THIS
  // session" independent of `sessionChanged`, and `startedAtRef` persists
  // the ORIGINAL load's start time across any such in-window reruns (rather
  // than restarting the clock), so a same-session refetch mid-floor still
  // respects the remaining time instead of bypassing or re-flooring it.
  const settledOnceRef = useRef(false)
  const startedAtRef = useRef(Date.now())

  // `generation` is a manual re-fetch trigger, not read in the effect body.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above.
  useEffect(() => {
    let cancelled = false
    let settleTimer: ReturnType<typeof setTimeout> | undefined
    // UID-335 — distinguishes a genuine navigation to a DIFFERENT session
    // (must show loading — the old session's data is irrelevant) from a
    // same-session background refetch (`refetch()`'s generation bump, incl.
    // the automatic focus/delay ones below): only the former resets `state`
    // to `loading`.
    const sessionChanged = prevSessionIdRef.current !== sessionId
    prevSessionIdRef.current = sessionId
    if (sessionChanged) {
      setState({ status: 'loading' })
      setSettling(false)
      startedAtRef.current = Date.now()
      settledOnceRef.current = false
    }
    // The `minLoadingMs` floor (UID-509) applies to the whole initial-load
    // window, not just its first `settle()` call — see this ref's own doc
    // comment above.
    const applyFloor = sessionChanged || !settledOnceRef.current

    // UID-583 (ADR-0173 D3) — the batch that created this session is still
    // running, so there is nothing worth reading yet. Return BEFORE the fetch
    // but AFTER the `sessionChanged` bookkeeping above, so a genuine
    // navigation still resets to `loading` and still restarts the floor
    // clock. The effect re-runs when this flips (it is in the dep array), and
    // that rerun sees `sessionChanged === false` with `settledOnceRef` still
    // unset — so `applyFloor` stays true and the floor is measured from
    // ARRIVAL, not from the moment the upload finished. That is deliberate:
    // the user has already been watching the panel for the whole upload, and
    // re-flooring here would add a second wait on top of a real one.
    if (waitForUpload) {
      return () => {
        cancelled = true
        clearTimeout(settleTimer)
      }
    }

    function settle(next: StagingState) {
      if (cancelled) return
      if (!applyFloor) {
        setState(next)
        return
      }
      setSettling(true)
      const finish = () => {
        setState(next)
        setSettling(false)
        settledOnceRef.current = true
      }
      const remaining = minLoadingMs - (Date.now() - startedAtRef.current)
      if (remaining <= 0) {
        finish()
      } else {
        settleTimer = setTimeout(() => {
          if (!cancelled) finish()
        }, remaining)
      }
    }

    serviceRef.current
      .getStagingTriage(sessionId)
      .then((triage) => {
        settle({ status: 'ready', triage })
      })
      .catch((error: unknown) => {
        if (error instanceof StagingSessionNotFoundError) {
          settle({ status: 'not_found' })
          return
        }
        if (!applyFloor) {
          // A background refetch that fails keeps showing the last good data
          // rather than clobbering it with an error the user never asked for.
          setState((prev) =>
            prev.status === 'ready'
              ? prev
              : {
                  status: 'error',
                  message:
                    error instanceof Error ? error.message : 'We couldn’t load this session.',
                },
          )
          return
        }
        settle({
          status: 'error',
          message: error instanceof Error ? error.message : 'We couldn’t load this session.',
        })
      })
    return () => {
      cancelled = true
      clearTimeout(settleTimer)
    }
    // `minLoadingMs` intentionally excluded from the deps below (same
    // suppression as `generation`, above) — it's a per-session floor read
    // via the persistent `startedAtRef`/`settledOnceRef`, not something a
    // caller changes on an already-mounted instance; including it would
    // re-run the fetch for no reason. `waitForUpload` (UID-583) IS in the
    // deps, and must be: unlike `minLoadingMs` it is read at fetch time and
    // flips on an already-mounted instance — that flip is exactly what
    // releases the held read.
  }, [sessionId, generation, waitForUpload])

  const refetch = useCallback(() => setGeneration((g) => g + 1), [])

  // UID-335 — this session's thumbnails may still be generating in the
  // background (UID-330's client-side render → presign → PUT) when this page
  // first loads right after upload. `useRefetchOnWindowFocus` catches the
  // tab-switch-away-and-back case; the poll below catches the far more common
  // case of staying on this same tab the whole time. `StagingPage` is
  // route-scoped (mounted only while viewing one session), unlike
  // `useUploadSessions`, so neither hook risks the Sidebar-wide-refetch
  // problem that one had to be moved out of.
  //
  // UID-335 follow-up — this used to be a single fixed-delay one-shot check
  // (a guess about generation time that a heavier render pipeline — shadow
  // maps, PMREM bake, occt WASM — can now miss, permanently, since nothing
  // ever re-checked). Polls every 2s while any identified/matched row is
  // still missing its thumbnail, capped at 20s so a render failure can't
  // poll forever.
  const stillGeneratingThumbnails =
    state.status === 'ready' &&
    (state.triage.identified.some((part) => part.thumbnailUrl == null) ||
      state.triage.matched.some((part) => part.thumbnailUrl == null))
  useRefetchOnWindowFocus(refetch)
  usePollWhilePending(refetch, stillGeneratingThumbnails, 2000, 20000)

  const applyMutation = useCallback(async (mutate: () => Promise<StagingTriage>) => {
    const triage = await mutate()
    setState({ status: 'ready', triage })
  }, [])

  return { state, settling, refetch, applyMutation }
}
